import { useState } from 'react';
import axios from 'axios';
import { X, CheckCircle, Loader2 } from 'lucide-react';
import { trackDemoEvent } from './demoAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { name: 'nombre', label: 'Nombre', required: true, half: true },
  { name: 'apellido', label: 'Apellido', required: true, half: true },
  { name: 'correo', label: 'Correo electrónico', required: true, type: 'email', half: true },
  { name: 'telefono', label: 'Teléfono', half: true },
  { name: 'cargo', label: 'Cargo', half: true },
  { name: 'institucion', label: 'Institución', required: true, half: true },
  { name: 'pais', label: 'País', half: true },
  { name: 'ciudad', label: 'Ciudad', half: true },
];

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/public/demo-request`, form);
      trackDemoEvent('demo_request_submitted', false);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo enviar la solicitud. Intente nuevamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Solicitar una demostración" data-testid="demo-request-modal">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-white text-lg font-bold">Solicita una demostración</h3>
          <button onClick={onClose} aria-label="Cerrar" className="text-slate-400 hover:text-white p-1" data-testid="close-demo-form-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-10 text-center" data-testid="demo-form-success">
            <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h4 className="text-white text-xl font-bold mb-2">¡Solicitud recibida!</h4>
            <p className="text-slate-300 text-sm mb-6">Nuestro equipo comercial te contactará a la brevedad para coordinar una demostración personalizada de SimuClinic.</p>
            <button onClick={onClose} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-6 py-3 rounded-xl">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map(f => (
              <div key={f.name} className={f.half ? '' : 'sm:col-span-2'}>
                <label htmlFor={`df-${f.name}`} className="block text-slate-300 text-xs font-medium mb-1">
                  {f.label}{f.required && <span className="text-cyan-400"> *</span>}
                </label>
                <input id={`df-${f.name}`} type={f.type || 'text'} required={f.required}
                  value={form[f.name] || ''} onChange={e => set(f.name, e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid={`demo-form-${f.name}`} />
              </div>
            ))}
            <div>
              <label htmlFor="df-tipo" className="block text-slate-300 text-xs font-medium mb-1">Tipo de institución</label>
              <select id="df-tipo" value={form.tipo_institucion || ''} onChange={e => set('tipo_institucion', e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                data-testid="demo-form-tipo_institucion">
                <option value="">Seleccionar...</option>
                <option>Universidad</option>
                <option>Instituto Profesional / CFT</option>
                <option>Hospital / Clínica</option>
                <option>Centro de Simulación</option>
                <option>Servicio de Salud</option>
                <option>Otra</option>
              </select>
            </div>
            <div>
              <label htmlFor="df-users" className="block text-slate-300 text-xs font-medium mb-1">Cantidad aproximada de usuarios</label>
              <select id="df-users" value={form.cantidad_usuarios || ''} onChange={e => set('cantidad_usuarios', e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                data-testid="demo-form-cantidad_usuarios">
                <option value="">Seleccionar...</option>
                <option>1 - 25</option>
                <option>26 - 100</option>
                <option>101 - 500</option>
                <option>Más de 500</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="df-msg" className="block text-slate-300 text-xs font-medium mb-1">Mensaje</label>
              <textarea id="df-msg" rows={3} value={form.mensaje || ''} onChange={e => set('mensaje', e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none"
                placeholder="Cuéntanos sobre tu institución y qué te gustaría ver en la demostración"
                data-testid="demo-form-mensaje" />
            </div>
            {error && <p className="sm:col-span-2 text-rose-400 text-sm" data-testid="demo-form-error">{error}</p>}
            <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="text-slate-300 hover:text-white text-sm font-medium px-5 py-3">Cancelar</button>
              <button type="submit" disabled={sending}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold px-6 py-3 rounded-xl transition-all disabled:opacity-60"
                data-testid="demo-form-submit">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                Enviar solicitud
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
